import React from 'react';

interface CustomerReference {
  id: number;
  name: string;
  position: string;
  company: string;
  testimonial: string;
  rating: number;
  logo?: string;
}

const CustomerReferences: React.FC = () => {
  const references: CustomerReference[] = [
    {
      id: 1,
      name: 'Anders Jonson',
      position: 'CTO',
      company: 'Bergenstråhle AB',
      testimonial: 'Jag har byggt 2 utvecklingsteam genom UtvecklareDirekt. Både team och individer har överlevererat och är nu en del av vår verksamhet. Dessutom är det kostnadseffektivt och flexibelt. Jag är helnöjd.',
      rating: 5,
      logo: '/images/bergenstrhle_partners_ab_logo.jpg'
    }
  ];

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, index) => (
          <svg
            key={index}
            className={`w-4 h-4 ${
              index < rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <section className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50 py-4">
      <div className="max-w-6xl mx-auto px-4">

        {/* References Grid */}
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
          {references.map((reference) => (
            <div
              key={reference.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
            >
              {/* Quote Icon */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <svg
                    className="w-8 h-8 text-primary-400 opacity-50"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                </div>

                <div className="flex-1">
                  {/* Testimonial */}
                  <p className="text-gray-700 text-sm leading-relaxed mb-4 italic">
                    "{reference.testimonial}"
                  </p>

                  {/* Rating */}
                  <div className="mb-4">
                    {renderStars(reference.rating)}
                  </div>

                  {/* Customer Info */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center space-x-3">
                      {reference.logo ? (
                        <div className="w-12 h-12 flex-shrink-0">
                          <img
                            src={reference.logo}
                            alt={`${reference.company} logo`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-700 font-semibold text-lg">
                            {reference.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          {reference.name}
                        </p>
                        <p className="text-gray-600 text-xs">
                          {reference.position}
                        </p>
                        <p className="text-primary-600 text-xs font-medium">
                          {reference.company}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Future References Placeholder - Hidden but structured for easy expansion */}
        <div className="hidden">
          {/* This structure can be easily activated when more references are added */}
          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              Fler referenser kommer snart...
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerReferences;